import React from "react";

/** Inline ingest progress widget — rendered on the Ingest Inbox row + the
 * IngestJob page header while `ingest_status === 'processing'`.
 *
 * `progress` comes from the job document's `ingest_progress` field:
 *   { phase: string, percent: number, message: string, started_at, updated_at }
 *
 * The widget gracefully degrades when `progress` is absent (e.g. an in-flight
 * job from before this feature shipped) — it just shows a generic
 * "Processing in background…" line.
 */
const PHASE_LABELS = {
  queued:                  "Queued for processing",
  extracting_zip:          "Unzipping the package",
  extracting_nested_zips:  "Unpacking nested attachments",
  classifying_files:       "Classifying files",
  parsed_claim_form:       "Claim form parsed",
  walking_evidence:        "Walking evidence folders",
  parsing_cet_workbooks:   "Parsing CET workbooks",
  finalising:              "Building summary",
  caching_cet_workings:    "Caching CET workings",
  complete:                "Ingest complete",
  error:                   "Ingest failed",
};

function fmtETA(progress) {
  if (!progress || !progress.started_at || !progress.percent || progress.percent >= 100) return "";
  try {
    const started = new Date(progress.started_at).getTime();
    const now = Date.now();
    const elapsed = (now - started) / 1000;
    if (elapsed < 5) return ""; // wait a bit before guessing
    const remaining = elapsed * (100 - progress.percent) / Math.max(progress.percent, 1);
    if (remaining < 10) return "<10 s left";
    if (remaining < 90) return `~${Math.round(remaining)} s left`;
    if (remaining < 600) return `~${Math.round(remaining / 60)} min left`;
    return `>${Math.round(remaining / 60)} min left`;
  } catch {
    return "";
  }
}

export function IngestProgressInline({ progress, jobId }) {
  if (!progress || typeof progress.percent !== "number") {
    return (
      <div className="text-[10px] text-blue-700 font-semibold tracking-[0.14em] uppercase mt-1" data-testid={`job-processing-${jobId}`}>
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse mr-1" />
        Processing in background…
      </div>
    );
  }
  const pct = Math.max(0, Math.min(100, progress.percent));
  const label = progress.message || PHASE_LABELS[progress.phase] || progress.phase || "Processing";
  const eta = fmtETA(progress);
  const tone = progress.phase === "error" ? "bg-red-500" : "bg-blue-500";
  return (
    <div className="mt-1.5 max-w-[280px]" data-testid={`ingest-progress-${jobId}`}>
      <div className="flex items-center justify-between text-[10px] font-mono text-slate-600 mb-0.5">
        <span data-testid={`ingest-progress-label-${jobId}`}>{label}</span>
        <span className="font-semibold text-slate-700" data-testid={`ingest-progress-percent-${jobId}`}>{pct}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden">
        <div
          className={`h-full ${tone} transition-all duration-300`}
          style={{ width: `${pct}%` }}
          data-testid={`ingest-progress-bar-${jobId}`}
        />
      </div>
      {eta && <div className="text-[10px] text-slate-500 font-mono mt-0.5" data-testid={`ingest-progress-eta-${jobId}`}>{eta}</div>}
    </div>
  );
}
